import {helpers} from 'vuelidate/lib/validators';

export const postalCode = (value: string) => {
  if (!helpers.req(value)) {
    return true;
  }

  const postalCodeRegexp = /^[0-9]{4}( ?)[a-zA-Z]{2}$/;

  return postalCodeRegexp.test(value);
}

export const houseNumber = (value: string) => {
  if (!helpers.req(value)) {
    return true;
  }

  const houseNumberRegexp = /^[0-9]+([a-zA-Z]+)?([0-9]+)?$/;

  return houseNumberRegexp.test(value)
}

export const internationalE164PhoneNumber = (value: string) => {
  if (!helpers.req(value)) {
    return true;
  }

  const phoneNumberRegexp = /^[+]31([0-7][0-9]{5}|8[58][0-9]{4})[0-9x]{3}$|^[+]31[89]0[069][0-9]{4,8}$|^[+]3197[0-9]{9}$|^[+]([0-24-9][0-9]|30|3[0-9])[0-9]{8,13}$/i;

  return phoneNumberRegexp.test(value);
};

export const iban = (value: string) => {
  if (!helpers.req(value)) {
    return true;
  }

  return /^(?:(?:IT|SM)\d{2}[A-Z]\d{22})$/i.test(value) ||
    /^(?:CY\d{2}[A-Z]\d{23})$/i.test(value) ||
    /^(?:NL\d{2}[A-Z]{4}\d{10})$/i.test(value) ||
    /^(?:LV\d{2}[A-Z]{4}\d{13})$/i.test(value) ||
    /^(?:BG|BH|GB|IE)\d{2}[A-Z]{4}\d{14}$/i.test(value) ||
    /^(?:GI\d{2}[A-Z]{4}\d{15})$/i.test(value) ||
    /^(?:RO\d{2}[A-Z]{4}\d{16})$/i.test(value) ||
    /^(?:KW\d{2}[A-Z]{4}\d{22})$/i.test(value) ||
    /^(?:MT\d{2}[A-Z]{4}\d{23})$/i.test(value) ||
    /^(?:NO\d{13})$/i.test(value) ||
    /^(?:(?:DK|FI|GL|FO)\d{16})$/i.test(value) ||
    /^(?:MK\d{17})$/i.test(value) ||
    /^(?:AT|EE|KZ|LU|XK)\d{18}$/i.test(value) ||
    /^(?:BA|HR|LI|CH|CR)\d{19}$/i.test(value) ||
    /^(?:(?:GE|DE|LT|ME|RS)\d{20})$/i.test(value) ||
    /^(?:IL\d{21})$/i.test(value) ||
    /^(?:AD|CZ|ES|MD|SA)\d{22}$/i.test(value) ||
    /^(?:PT\d{23})$/i.test(value) ||
    /^(?:BE|IS)\d{24}$/i.test(value);
};

export const xlsx = (value: any) => {
  if (!helpers.req(value)) {
    return true;
  }

  return value.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

export const phoneNumberRange = (phoneNumberRangeFrom: string, phoneNumberRangeTo: string, numberBlockSize: any) =>
  helpers.withParams(
    { type: 'phoneNumberRange' },
    () => {
      switch (numberBlockSize) {
        case 10: {
          return validate10NumberBlockSize(phoneNumberRangeFrom, phoneNumberRangeTo);
        }
        case 100: {
          return validate100NumberBlockSize(phoneNumberRangeFrom, phoneNumberRangeTo);
        }
        case 1000: {
          return validate1000NumberBlockSize(phoneNumberRangeFrom, phoneNumberRangeTo);
        }
        default: {
          return false;
        }
      }
    },
  );

function validate10NumberBlockSize(phoneNumberRangeFrom: string, phoneNumberRangeTo: string): boolean
{
  // remove last character from number block and compare first n-1 characters
  if (phoneNumberRangeFrom.slice(0, -1) !== phoneNumberRangeTo.slice(0, -1)) {
    return false;
  }

  // compare last character form number block
  if (
    '0' === phoneNumberRangeFrom.slice(-1)
    && '9' === phoneNumberRangeTo.slice(-1)
  ) {
    return true;
  }

  return false;
}

function validate100NumberBlockSize(phoneNumberRangeFrom: string, phoneNumberRangeTo: string): boolean
{
  // remove last 2 characters from number block and compare first n-2 characters
  if (phoneNumberRangeFrom.slice(0, -2) !== phoneNumberRangeTo.slice(0, -2)) {
    return false;
  }

  // compare last 2 characters form number block
  if (
    '00' === phoneNumberRangeFrom.slice(-2)
    && '99' === phoneNumberRangeTo.slice(-2)
  ) {
    return true;
  }

  return false;
}

function validate1000NumberBlockSize(phoneNumberRangeFrom: string, phoneNumberRangeTo: string): boolean
{
  // remove last 3 characters from number block and compare first n-3 characters
  if (phoneNumberRangeFrom.slice(0, -3) !== phoneNumberRangeTo.slice(0, -3)) {
    return false;
  }

  // compare last 3 characters form number block
  if (
    '000' === phoneNumberRangeFrom.slice(-3)
    && '999' === phoneNumberRangeTo.slice(-3)
  ) {
    return true;
  }

  return false;
}

export const agreementEndDateValidator = (agreementStartDate: string | null) =>
  helpers.withParams(
    { type: 'agreementEndDateValidator' },
    (agreementEndDate: string) => {
      if (!agreementEndDate) {
        return true;
      }

      if (!agreementStartDate) {
        return true;
      }

      return new Date(agreementEndDate).setHours(0,0,0,0) >= new Date(agreementStartDate).setHours(0,0,0,0);
    },
  );

export const wishDateValidator = (productMinDate: string | null, productMaxDate: string | null) =>
  helpers.withParams(
    { type: 'wishDateValidator' },
    (wishDate: string | null) => {
      if (!wishDate) {
        return true;
      }
      const date = new Date(wishDate).setHours(0,0,0,0);
      const now = new Date().setHours(0,0,0,0);
      const min = productMinDate ? new Date(productMinDate).setHours(0,0,0,0) : null;
      const max = productMaxDate ? new Date(productMaxDate).setHours(0,0,0,0) : null;

      if (!productMinDate && now > date) {
        return false;
      }

      if (min && date < min) {
        return false;
      }

      if (max && date > max) {
        return false;
      }

      return true;
    },
  );